import { useEffect, useState } from "react";

import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { FIELD } from "../ui/classes";
import { Spinner } from "../ui/Spinner";
import { Banner } from "../ui/Banner";
import { LOGO_SRC } from "../ui/logo";

import {
  fetchProjectsResult,
  hasProjectAccess,
  type Project,
} from "./useProjects";

interface PickerPageProps {
  /**
   * Phase 9 — picking a project navigates to the per-project chat
   * landing rather than auto-creating a session. The landing page
   * surfaces prior sessions + a ResumeBanner + an explicit "Start
   * session" CTA.
   */
  readonly onProjectPicked: (projectId: number) => void;
}

const formatActivity = (iso?: string): string => {
  if (iso === undefined) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export function PickerPage({ onProjectPicked }: PickerPageProps) {
  const t = useT();
  const [projects, setProjects] = useState<Project[] | null>(null);
  // Whether the server enforces per-project access. Drives whether a null
  // `loomLevel` means "no access" (hide the card) or "enforcement off".
  const [accessEnforced, setAccessEnforced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [openError, setOpenError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const q = filter.trim();
    // Server-side narrowing (mirrors the Focus picker): empty filter loads
    // the most-recently-active projects immediately; typing debounces a
    // `search` round-trip. GitLab does the walk + matching, not a buffered
    // client list. Previous results stay visible during a refetch.
    const run = () =>
      fetchProjectsResult(q === "" ? { recent: true } : { search: q })
        .then((next) => {
          if (cancelled) return;
          setProjects(next.projects);
          setAccessEnforced(next.accessEnforced);
          setError(null);
        })
        .catch((err: Error) => {
          if (!cancelled) setError(err.message);
        });
    const handle = setTimeout(run, q === "" ? 0 : 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [filter]);

  const openProject = (project: Project) => {
    setOpenError(null);
    try {
      onProjectPicked(project.id);
    } catch (err) {
      setOpenError((err as Error).message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <header className="mb-6 flex items-center gap-3">
        <img
          src={LOGO_SRC}
          alt=""
          aria-hidden
          className="h-10 w-10 shrink-0 rounded-lg"
        />
        <div>
          <h1 className="text-2xl font-semibold">{t("projects.pickTitle")}</h1>
        </div>
      </header>

      {openError !== null && (
        <div className="mb-4">
          <Banner tone="error">{t("projects.openSessionFailed", { error: openError })}</Banner>
        </div>
      )}

      <input
        aria-label={t("projects.filterAriaLabel")}
        placeholder={t("projects.filterPlaceholder")}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className={`mb-4 w-full ${FIELD}`}
      />

      {projects === null && !error && <Spinner label={t("projects.loadingProjects")} />}
      {error !== null && (
        <Banner tone="error">{t("projects.loadProjectsFailed", { error })}</Banner>
      )}
      {/* Access-control UX (server-authoritative): with enforcement on, projects
          the user has no Loom access to (loomLevel === null) are hidden from the
          picker entirely. There is no Implement affordance HERE — Implement
          lives in the Focus intro step (FocusPage), gated server-side; the
          per-level `canImplement` gate belongs there if/when the Focus picker
          surfaces it inline. */}
      {projects !== null &&
        projects.filter((p) => hasProjectAccess(p, accessEnforced)).length ===
          0 && (
          <p className="text-sm text-ink-subtle">{t("projects.noProjectsMatch")}</p>
        )}
      {projects !== null && (
        <ul className="space-y-2">
          {projects
            .filter((p) => hasProjectAccess(p, accessEnforced))
            .map((p) => (
            <li key={p.id}>
              <Card className="flex items-center justify-between p-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{p.name_with_namespace}</div>
                  {p.last_activity_at !== undefined && (
                    <div className="text-xs text-ink-subtle">
                      {t("projects.lastActivity", { when: formatActivity(p.last_activity_at) })}
                    </div>
                  )}
                </div>
                <Button onClick={() => openProject(p)}>{t("projects.open")}</Button>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
